import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './KitchenReceipt.css';

const KitchenReceipt = forwardRef(({ order, orders = [] }, ref) => {
    const { t, i18n } = useTranslation();
    const receiptRef = useRef(null);

    const locale = i18n.language === 'fa' ? 'fa-IR' : 'en-US';

    const formatNumber = (num) => new Intl.NumberFormat(locale).format(Number(num ?? 0));

    const isTakeAway = order?.table === null || order?.table === undefined;

    const orderType = isTakeAway ? t('take_away') : t('dine_in');

    const customerName =
        order?.customer_name?.trim() || t('anonymous_customer') || 'Anonymous Customer';

    const formatDate = (dateString) => {
        if (!dateString) return '-';

        return new Date(dateString).toLocaleDateString(locale);
    };

    const formatTime = (dateString) => {
        if (!dateString) return '-';

        return new Date(dateString).toLocaleTimeString(locale, {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        });
    };
    const getDailyOrderNumber = (currentOrder) => {
        if (!currentOrder || !orders.length) {
            return '-';
        }

        const orderDate = new Date(currentOrder.created_at).toDateString();

        const sameDayOrders = orders
            .filter((item) => new Date(item.created_at).toDateString() === orderDate)
            .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

        const index = sameDayOrders.findIndex((item) => item.id === currentOrder.id);

        return index !== -1 ? index + 1 : '-';
    };

    const printStyles = `
        @font-face {
            font-family: "Vazir";
            src: url("${window.location.origin}/fonts/Vazir.ttf")
                format("truetype");
            font-weight: normal;
            font-style: normal;
        }

        @page {
            size: 80mm auto;
            margin: 0;
        }

        * {
            box-sizing: border-box;
        }

        html,
        body {
            width: 80mm;
            margin: 0;
            padding: 0;
            background: #fff;
        }

        body {
            font-family: "Vazir", sans-serif;
            color: #000;
            direction: rtl;
            line-height: 1.4;
            font-size: 13px;

            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
        }

        .kitchen_receipt {
            max-width: 60mm;

            margin: 0;
            padding: 4mm;

            background: #fff;
            color: #000;

            font-family: "Vazir", sans-serif;
            direction: rtl;
            line-height: 1.4;

            box-sizing: border-box;
        }

        .kitchen_receipt_header {
            text-align: center;
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 12px;
        }

        .kitchen_receipt_info {
            display: flex;
            direction: ltr;
            width: 100%;
            justify-content: space-between;
        }

        .kitchen_receipt_info_box {
            flex: 1;
            border: 1px solid #000;
            padding: 6px 4px;
            display: flex;
            flex-direction: column;
            max-width: 5rem;
            text-align: center;
            justify-content: center;
            min-width: 0;
        }

        .kitchen_receipt_info_label {
            display: block;
            font-size: 11px;
            margin-bottom: 2px;
        }

        .kitchen_receipt_info_value {
            display: block;
            font-size: 13px;
            font-weight: bold;
        }

        .kitchen_receipt_separator {
            border-top: 1px solid #000;
            margin: 10px 0;
        }

        .kitchen_receipt_customer {
            text-align: center;
            font-size: 14px;
            font-weight: bold;
            margin: 6px 0;
        }

        .kitchen_receipt_table {
            width: 100%;
            border-collapse: collapse;
            table-layout: fixed;
            margin-top: 8px;
        }

        .kitchen_receipt_table th,
        .kitchen_receipt_table td {
            padding: 6px 3px;
            font-size: 13px;
            border-bottom: 1px dotted #999;
        }

        .kitchen_receipt_table th {
            font-weight: bold;
            border-bottom: 1px solid #000;
        }

        .kitchen_receipt_product {
            width: 78%;
            text-align: right;
            word-break: break-word;
        }

        .kitchen_receipt_quantity {
            width: 22%;
            text-align: center;
        }

        .kitchen_receipt_table tr {
            break-inside: avoid;
            page-break-inside: avoid;
        }
    `;

    const handlePrint = () => {
        if (!receiptRef.current || !order) {
            return;
        }

        const printContent = receiptRef.current.outerHTML;

        const printWindow = window.open('', '_blank', 'width=400,height=600');

        if (!printWindow) {
            console.error('Could not open print window.');
            return;
        }

        printWindow.document.open();

        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
                <head>
                    <meta charset="UTF-8" />

                    <title>
                        Kitchen Order ${order.order_id || 'receipt'}
                    </title>

                    <style>
                        ${printStyles}
                    </style>
                </head>

                <body>
                    ${printContent}
                </body>
            </html>
        `);

        printWindow.document.close();

        printWindow.focus();

        printWindow.onload = () => {
            setTimeout(() => {
                printWindow.print();
                printWindow.close();
            }, 300);
        };
    };

    useImperativeHandle(ref, () => ({
        print: handlePrint,
    }));

    if (!order) {
        return null;
    }

    return (
        <div ref={receiptRef} className="kitchen_receipt">
            {/* Header */}
            <div className="kitchen_receipt_header">
                {t('kitchen')} - {orderType}
            </div>

            {/* Date / Time + Order ID */}
            <div className="kitchen_receipt_info">
                <div className="kitchen_receipt_info_box">
                    <span className="kitchen_receipt_info_value">
                        {formatDate(order.created_at)}
                    </span>
                    <span className="kitchen_receipt_info_value">
                        {formatTime(order.created_at)}
                    </span>
                </div>

                <div className="kitchen_receipt_info_box">
                    <span className="kitchen_receipt_info_value">
                        {formatNumber(getDailyOrderNumber(order))}
                    </span>
                </div>
            </div>

            {/* Separator */}
            <div className="kitchen_receipt_separator" />

            {/* Customer */}
            <div className="kitchen_receipt_customer">
                <span>{t('customer')}</span>:<span> {customerName}</span>
            </div>

            {/* Separator */}
            <div className="kitchen_receipt_separator" />

            {/* Items */}
            <table className="kitchen_receipt_table">
                <thead>
                    <tr>
                        <th className="kitchen_receipt_product">{t('item') || 'Product'}</th>

                        <th className="kitchen_receipt_quantity">{t('quantity') || 'Quantity'}</th>
                    </tr>
                </thead>

                <tbody>
                    {Array.isArray(order.items) &&
                        order.items.map((item) => (
                            <tr key={item.id}>
                                <td className="kitchen_receipt_product">{item.item_title}</td>

                                <td className="kitchen_receipt_quantity">
                                    {formatNumber(item.quantity)}
                                </td>
                            </tr>
                        ))}
                </tbody>
            </table>
        </div>
    );
});

KitchenReceipt.displayName = 'KitchenReceipt';

export default KitchenReceipt;
